import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Toast } from 'src/components/shared/NotificationToast';
import { getStoredSignupToken } from './auth/signupOnboarding';
import SignupPageShell from './SignupPageShell';

const RegisterCustomPlanRequest = () => {
  const [searchParams] = useSearchParams();
  const signupToken = String(searchParams.get('st') || '').trim() || getStoredSignupToken();
  const planLink = signupToken
    ? `/auth/register/plans?st=${encodeURIComponent(signupToken)}`
    : '/auth/register/plans';
  const [loading, setLoading] = useState(true);
  const [customPlanRequestStatus, setCustomPlanRequestStatus] = useState('none');
  const [hasAssignedCustomPlan, setHasAssignedCustomPlan] = useState(false);

  useEffect(() => {
    let active = true;

    const loadPlanState = async () => {
      if (!signupToken) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/plans`, {
          params: { signupToken },
        });

        if (!active) {
          return;
        }

        setCustomPlanRequestStatus(String(data.customPlanRequestStatus || 'none').trim().toLowerCase() || 'none');
        setHasAssignedCustomPlan(Boolean(data.hasAssignedCustomPlan));
      } catch (error) {
        if (!active) {
          return;
        }
        Toast({ type: 'error', message: error.response?.data?.message || error.message });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPlanState();

    return () => {
      active = false;
    };
  }, [signupToken]);

  const isReady = customPlanRequestStatus === 'done' && hasAssignedCustomPlan;
  const pageTitle = isReady ? 'Your plan is ready' : 'Custom plan request received';
  const pageDescription = isReady ? 'Your custom plan is available now' : 'Your request is being processed';
  const primaryMessage = useMemo(() => {
    if (isReady) {
      return 'Your plan is ready. Return to the plans page to review the assigned custom plan and continue signup.';
    }
    return "We've received your custom plan request. Your custom plan will appear here once it's ready.";
  }, [isReady]);
  const secondaryMessage = isReady
    ? 'You can now continue with the available plans, including your assigned custom plan.'
    : 'You can check back shortly or continue with the available plans.';

  return (
    <SignupPageShell title={pageTitle} description={pageDescription}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {isReady ? <Alert severity="success">Your assigned custom plan is now available on the plans page.</Alert> : null}

          <Typography variant="h3">{pageTitle}</Typography>

          <Typography color="text.secondary">
            {primaryMessage}
          </Typography>

          <Typography color="text.secondary">
            {secondaryMessage}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button component={Link} to={planLink} variant="contained">
              {isReady ? 'View Plans' : 'Check Plans'}
            </Button>
            <Button component={Link} to="/auth/login" variant="outlined">
              Back to Login
            </Button>
          </Stack>
        </Stack>
      )}
    </SignupPageShell>
  );
};

export default RegisterCustomPlanRequest;
